/**
 * CustomCursor — Replaces the OS cursor with a glowing W-logo that follows
 * the mouse. CSS `cursor: url(...)` cannot apply box-shadow/drop-shadow, so
 * we render the logo as a DOM element with a soft white halo behind it.
 *
 * - Hides on touch devices (pointer: coarse)
 * - Hides while typing in <input>/<textarea>
 * - Scales up subtly on interactive elements
 * - Uses requestAnimationFrame for buttery 60fps tracking
 */
import { useEffect, useRef } from "react";

export default function CustomCursor() {
  const cursorRef = useRef(null);
  const targetRef = useRef({ x: -100, y: -100 });
  const posRef = useRef({ x: -100, y: -100 });
  const rafRef = useRef(null);
  const hoveringInteractiveRef = useRef(false);

  useEffect(() => {
    // Skip on touch-only devices
    if (typeof window === "undefined") return;
    const isCoarse = window.matchMedia && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    if (isCoarse) return;

    const cursor = cursorRef.current;
    if (!cursor) return;

    const onMove = (e) => {
      targetRef.current = { x: e.clientX, y: e.clientY };
      // Track whether we're over an interactive element
      const t = e.target;
      const interactive =
        t && (t.closest("a, button, [role='button'], summary, label, .cursor-pointer, input[type='submit'], input[type='button'], select, input[type='checkbox'], input[type='radio'], input[type='range']"));
      hoveringInteractiveRef.current = !!interactive;
      const overText = t && t.matches && t.matches("input[type='text'], input[type='email'], input[type='password'], input[type='search'], input[type='url'], input[type='number'], input[type='tel'], textarea");
      cursor.style.opacity = overText ? "0" : "1";
    };

    const onLeave = () => {
      cursor.style.opacity = "0";
    };
    const onEnter = () => {
      cursor.style.opacity = "1";
    };

    const tick = () => {
      // Eased follow for smoothness
      const t = targetRef.current;
      const p = posRef.current;
      p.x += (t.x - p.x) * 0.32;
      p.y += (t.y - p.y) * 0.32;
      const scale = hoveringInteractiveRef.current ? 1.25 : 1;
      cursor.style.transform = `translate3d(${p.x - 20}px, ${p.y - 20}px, 0) scale(${scale})`;
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    rafRef.current = requestAnimationFrame(tick);

    // Mark body so global CSS can hide the OS cursor
    document.body.classList.add("nm-custom-cursor-active");

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.body.classList.remove("nm-custom-cursor-active");
    };
  }, []);

  return (
    <div
      ref={cursorRef}
      data-testid="custom-cursor"
      aria-hidden="true"
      className="nm-cursor"
    >
      <img src="/logo.png?v=4" alt="" draggable="false" />
    </div>
  );
}
